import { useState, useEffect } from 'react';
import type { Desafio } from '../../types/quiz.types';
import { shuffleArray } from '../../utils/quizUtils';
import { ArrowUp, ArrowDown } from 'lucide-react';

interface Props {
  desafio: Desafio;
  onComplete: (sucesso: boolean) => void;
}

export function LinhaDoTempoMinigame({ desafio, onComplete }: Props) {
  const itens = desafio.itens ?? [];
  const recompensa = desafio.recompensa?.moedas ?? 0;

  const [itensEmbaralhados, setItensEmbaralhados] = useState<{ texto: string; ano: number; idOriginal: number }[]>([]);

  useEffect(() => {
    if (itens.length > 0) {
      const mapeados = itens.map((item, idOriginal) => ({ ...item, idOriginal }));
      setItensEmbaralhados(shuffleArray(mapeados));
    }
  }, [itens]);

  const mover = (index: number, direcao: 'up' | 'down') => {
    if (direcao === 'up' && index === 0) return;
    if (direcao === 'down' && index === itensEmbaralhados.length - 1) return;

    const novaLista = [...itensEmbaralhados];
    const targetIndex = direcao === 'up' ? index - 1 : index + 1;

    [novaLista[index], novaLista[targetIndex]] = [novaLista[targetIndex], novaLista[index]];
    setItensEmbaralhados(novaLista);
  };

  const handleValidar = () => {
    let tudoCerto = true;
    for (let i = 0; i < itensEmbaralhados.length - 1; i++) {
      if (itensEmbaralhados[i].ano > itensEmbaralhados[i + 1].ano) {
        tudoCerto = false;
        break;
      }
    }
    onComplete(tudoCerto);
  };

  return (
    <div className="w-full flex flex-col justify-between gap-2 sm:gap-3 select-none">
      <p className="text-slate-300 text-fluid-body leading-tight text-center sm:text-left font-medium">
        {desafio.instrucao}
      </p>

      <div className="flex flex-col gap-1.5 sm:gap-2">
        {itensEmbaralhados.length > 0 ? (
          itensEmbaralhados.map((item, index) => (
            <div
              key={item.idOriginal}
              className="px-2.5 sm:px-3 py-1.5 sm:py-2 bg-slate-900/90 rounded-xl border border-slate-700/80 text-left text-slate-200 flex items-center justify-between shadow-md gap-2 transition-all hover:border-sky-500/50"
            >
              <div className="flex-1 text-fluid-sm font-medium leading-snug">
                {item.texto}
              </div>

              {/* Setas lado a lado com alvo de toque ≥44px (L7) sem esticar a
                  linha na vertical como duas setas empilhadas de 44px fariam */}
              <div className="flex flex-row gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => mover(index, 'up')}
                  disabled={index === 0}
                  className={`w-11 h-11 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-600 transition-colors ${index === 0 ? 'opacity-20 cursor-not-allowed' : 'hover:border-amber-400'}`}
                  title="Mover para cima"
                >
                  <ArrowUp size={16} className="text-amber-400" />
                </button>
                <button
                  type="button"
                  onClick={() => mover(index, 'down')}
                  disabled={index === itensEmbaralhados.length - 1}
                  className={`w-11 h-11 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-600 transition-colors ${index === itensEmbaralhados.length - 1 ? 'opacity-20 cursor-not-allowed' : 'hover:border-amber-400'}`}
                  title="Mover para baixo"
                >
                  <ArrowDown size={16} className="text-amber-400" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-slate-500 text-xs italic text-center py-4">Preparações temporais...</p>
        )}
      </div>

      <button
        onClick={handleValidar}
        className="btn-cyber w-full py-2.5 sm:py-3 mt-1 rounded-xl shadow-lg shadow-emerald-950/50 bg-emerald-500 text-slate-950 font-cyber font-black hover:bg-emerald-400 transition-colors uppercase tracking-wider text-fluid-body shrink-0 border border-emerald-300"
      >
        Confirmar Ordem {recompensa > 0 ? `(+${recompensa} Axé)` : ''}
      </button>
    </div>
  );
}
